/**
 * Admin Analytics Dashboard
 * Overview statistics and charts for the marketplace
 */
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Users, Package, TrendingUp, DollarSign, Eye, Star,
  Loader2, RefreshCw, ArrowUp, ArrowDown, Briefcase, MessageSquare, Gavel
} from 'lucide-react';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, BarChart, Bar 
} from 'recharts';
import { useAdmin } from './AdminContext';

const StatCard = ({ title, value, change, icon: Icon, color = 'primary', loading, subtitle }) => {
  // Map color names to actual Tailwind classes
  const colorClasses = {
    'blue-500': { bg: 'bg-blue-500/10', text: 'text-blue-500', border: 'border-blue-500/20' },
    'green-500': { bg: 'bg-green-500/10', text: 'text-green-500', border: 'border-green-500/20' },
    'purple-500': { bg: 'bg-purple-500/10', text: 'text-purple-500', border: 'border-purple-500/20' },
    'amber-500': { bg: 'bg-amber-500/10', text: 'text-amber-500', border: 'border-amber-500/20' },
    'primary': { bg: 'bg-primary/10', text: 'text-primary', border: 'border-primary/20' },
    'cyan-500': { bg: 'bg-cyan-500/10', text: 'text-cyan-500', border: 'border-cyan-500/20' },
    'pink-500': { bg: 'bg-pink-500/10', text: 'text-pink-500', border: 'border-pink-500/20' },
    'orange-500': { bg: 'bg-orange-500/10', text: 'text-orange-500', border: 'border-orange-500/20' },
  };
  
  const colors = colorClasses[color] || colorClasses['primary'];
  
  return (
    <div className={`bg-card border border-border rounded-2xl p-5 hover:shadow-lg hover:border-border/80 transition-all duration-200 group`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          {loading ? (
            <div className="h-9 flex items-center">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <p className="text-3xl font-bold mt-1 tracking-tight">{value}</p>
          )}
          {subtitle && !loading && (
            <p className={`text-sm mt-1 ${colors.text}`}>{subtitle}</p>
          )}
          {change !== undefined && !loading && (
            <div className={`flex items-center gap-1 text-sm mt-2 ${change >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              {change >= 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
              <span className="font-medium">{Math.abs(change)}%</span>
              <span className="text-muted-foreground">vs last week</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-xl ${colors.bg} ${colors.border} border group-hover:scale-110 transition-transform duration-200`}>
          <Icon className={`w-6 h-6 ${colors.text}`} />
        </div>
      </div>
    </div>
  );
};

const AdminAnalytics = () => {
  const { apiCall, getCached, setCached } = useAdmin();
  
  const [analytics, setAnalytics] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const fetchAnalytics = useCallback(async (forceRefresh = false) => {
    // Check cache first
    const cachedAnalytics = getCached('analytics');
    const cachedStats = getCached('stats');
    
    if (!forceRefresh && cachedAnalytics && cachedStats) {
      setAnalytics(cachedAnalytics);
      setStats(cachedStats);
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const [analyticsRes, statsRes] = await Promise.all([
        apiCall('get', '/api/admin/analytics'),
        apiCall('get', '/api/admin/stats')
      ]);
      
      setAnalytics(analyticsRes);
      setStats(statsRes);
      setCached('analytics', analyticsRes);
      setCached('stats', statsRes);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  }, [apiCall, getCached, setCached]);
  
  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);
  
  // Format large numbers
  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toString() || '0';
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Analytics Dashboard</h2>
          <p className="text-sm text-muted-foreground">Marketplace overview and performance metrics</p>
        </div>
        <button
          onClick={() => fetchAnalytics(true)}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-secondary rounded-lg hover:bg-secondary/80 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>
      
      {/* Stats Cards - Primary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Users"
          value={formatNumber(analytics?.overview?.total_users || 0)}
          change={analytics?.users_growth}
          icon={Users}
          color="blue-500"
          loading={loading}
        />
        <StatCard
          title="Active Listings"
          value={formatNumber(analytics?.overview?.total_listings || 0)}
          change={analytics?.listings_growth}
          icon={Package}
          color="green-500"
          loading={loading}
        />
        <StatCard
          title="Total Views"
          value={formatNumber(analytics?.overview?.total_page_views || 0)}
          icon={Eye}
          color="purple-500"
          loading={loading}
        />
        <StatCard
          title="Avg. Rating"
          value={analytics?.avg_rating?.toFixed(1) || '0.0'}
          icon={Star}
          color="amber-500"
          loading={loading}
        />
      </div>
      
      {/* Stats Cards - Secondary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Business Users"
          value={formatNumber(analytics?.users?.business || stats?.business_users || 0)}
          icon={Briefcase}
          color="cyan-500"
          loading={loading}
        />
        <StatCard
          title="Verified Businesses"
          value={formatNumber(analytics?.users?.verified_businesses || stats?.verified_businesses || 0)}
          icon={Users}
          color="primary"
          loading={loading}
        />
        <StatCard
          title="Active Auctions"
          value={formatNumber(analytics?.listings_by_type?.auction || stats?.active_auctions || 0)}
          icon={Gavel}
          color="orange-500"
          loading={loading}
        />
        <StatCard
          title="Total Messages"
          value={formatNumber(analytics?.overview?.total_messages || stats?.total_messages || 0)}
          icon={MessageSquare}
          color="pink-500"
          loading={loading}
        />
      </div>
      
      {/* Charts */}
      {!loading && analytics && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Category Distribution */}
          {analytics.categories && Object.keys(analytics.categories).length > 0 && (
            <div className="bg-card border border-border rounded-2xl p-5">
              <h3 className="font-semibold mb-4 text-lg">Listings by Category</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={Object.entries(analytics.categories).slice(0, 10).map(([name, count]) => ({ name, count }))}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="name" className="text-xs" angle={-45} textAnchor="end" height={80} />
                  <YAxis className="text-xs" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }} 
                  />
                  <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          
          {/* Island Distribution */}
          {analytics.listings_by_island && Object.keys(analytics.listings_by_island).length > 0 && (
            <div className="bg-card border border-border rounded-2xl p-5">
              <h3 className="font-semibold mb-4 text-lg">Listings by Island</h3>
              <div className="grid grid-cols-3 gap-4">
                {Object.entries(analytics.listings_by_island).map(([island, count]) => (
                  <div key={island} className="text-center p-5 bg-secondary/30 rounded-xl hover:bg-secondary/50 transition-colors">
                    <div className="text-3xl mb-2">
                      {island === 'curacao' ? '🇨🇼' : 
                       island === 'aruba' ? '🇦🇼' : 
                       island === 'bonaire' ? '🇧🇶' : '🏝️'}
                    </div>
                    <div className="font-semibold capitalize text-sm">{island}</div>
                    <div className="text-2xl font-bold text-primary mt-1">{formatNumber(count)}</div>
                    <div className="text-xs text-muted-foreground">listings</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Listings Over Time - only show if data exists */}
          {analytics.listings_over_time && analytics.listings_over_time.length > 0 && (
            <div className="bg-card border border-border rounded-2xl p-5 lg:col-span-2">
              <h3 className="font-semibold mb-4 text-lg">Listings Over Time</h3>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={analytics.listings_over_time}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="date" className="text-xs" />
                  <YAxis className="text-xs" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '12px'
                    }} 
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="listings" 
                    stroke="hsl(var(--primary))" 
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminAnalytics;
